import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'

const baseURL = process.env.REACT_APP_BASE_URL
const designationsURL = `${baseURL}/api/designation/get-list?isActive=true`
const roleDesignationsURL = `${baseURL}/api/designation/get-list-on-role?isActive=true`
const createDesignationURL = `${baseURL}/api/designation/create`
const deleteDesignationURL = (designationId) =>
  `${baseURL}/api/designation/delete?designationId=${designationId}`
const updateDesignationURL = `${baseURL}/api/designation/update`
const sponsorByDesignationURL = (sponsorBy) =>
  `${baseURL}/api/designation/get-list-on-sponsorby?sponsorBy=${sponsorBy}&isActive=true`

export const fetchDesignations = createAsyncThunk(
  'designations/fetchDesignations',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const accessToken = state.auth.accessToken

      const response = await axios.get(designationsURL, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred')
    }
  }
)

export const fetchRoleDesignations = createAsyncThunk(
  'designations/fetchRoleDesignations',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const accessToken = state.auth.accessToken

      const response = await axios.get(roleDesignationsURL, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred')
    }
  }
)

export const createDesignation = createAsyncThunk(
  'designations/createDesignation',
  async (designation, { rejectWithValue, dispatch, getState }) => {
    try {
      const state = getState()
      const accessToken = state.auth.accessToken

      const designationWithId = {
        id: 0,
        percentage: Number(designation.percentage),
        code: designation.code,
        name: designation.name,
        isActive: true,
      }

      const response = await axios.post(
        createDesignationURL,
        designationWithId,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )

      if (response.data.isSuccess) {
        dispatch(fetchDesignations())
      } else {
        console.error(
          'Error: The backend did not indicate success:',
          response.data
        )
      }
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred')
    }
  }
)

export const deleteDesignation = createAsyncThunk(
  'designations/deleteDesignation',
  async (designationId, { rejectWithValue, getState }) => {
    try {
      const state = getState()
      const accessToken = state.auth.accessToken

      const response = await axios.get(deleteDesignationURL(designationId), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      })

      if (!response.data.isSuccess) {
        return rejectWithValue(response.data)
      }

      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred')
    }
  }
)

export const fetchSponsorDesignation = createAsyncThunk(
  'designations/fetchSponsorDesignation',
  async (sponsorBy, { rejectWithValue, getState }) => {
    // debugger
    try {
      const state = getState()
      const accessToken = state.auth.accessToken

      const response = await axios.get(sponsorByDesignationURL(sponsorBy), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      })

      if (!response.data.isSuccess) {
        return rejectWithValue(response.data)
      }

      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred')
    }
  }
)

export const updateDesignation = createAsyncThunk(
  'designations/updateDesignation',
  async (designation, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState()
      const accessToken = state.auth.accessToken

      const response = await axios.put(
        updateDesignationURL,
        {
          id: designation.id,
          code: designation.code,
          name: designation.name,
          percentage: designation.percentage,
          isActive: designation.isActive || true,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        }
      )

      if (!response.data.isSuccess) {
        return rejectWithValue(response.data || 'Failed to update designation')
      }
      dispatch(fetchDesignations())
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || 'An error occurred')
    }
  }
)

const designationsApiSlice = createSlice({
  name: 'designations',
  initialState: {
    list: [],
    status: 'idle',
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDesignations.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(fetchDesignations.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.list = action.payload.data || []
      })
      .addCase(fetchDesignations.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload || 'An unexpected error occurred.'
      })
      .addCase(fetchRoleDesignations.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(fetchRoleDesignations.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.list = action.payload.data || []
      })
      .addCase(fetchRoleDesignations.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload || 'An unexpected error occurred.'
      })
      .addCase(fetchSponsorDesignation.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(fetchSponsorDesignation.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.list = action.payload.data || []
      })
      .addCase(fetchSponsorDesignation.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload || 'An unexpected error occurred.'
      })
      .addCase(createDesignation.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(createDesignation.fulfilled, (state, action) => {
        state.status = 'succeeded'
      })
      .addCase(createDesignation.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload || 'An unexpected error occurred.'
      })
      .addCase(deleteDesignation.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(deleteDesignation.fulfilled, (state, action) => {
        state.status = 'succeeded'
      })
      .addCase(deleteDesignation.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload || 'An unexpected error occurred.'
      })
      .addCase(updateDesignation.pending, (state) => {
        state.status = 'loading'
        state.error = null
      })
      .addCase(updateDesignation.fulfilled, (state, action) => {
        state.status = 'succeeded'
        const updatedDesignation = action.payload.data
        const index = state.list.findIndex(
          (item) => item.id === updatedDesignation.id
        )
        if (index !== -1) {
          state.list[index] = updatedDesignation
        }
      })
      .addCase(updateDesignation.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.payload || 'Failed to update designation'
      })
      .addCase('RESET', () => ({
        list: [],
        status: 'idle',
        error: null,
      }))
  },
})

export default designationsApiSlice.reducer
